import * as React from 'react';

import { translate } from '~l10n/client';

import { SmallButton, UIButton } from '~shared/components/buttons';
import { Container } from '~shared/components/Container/Container';
import { DialogConfirmation } from '~shared/components/Dialog';
import { WorkspaceInviteDialog } from '~teacher/components/workspaces/InviteWorkspaceDialog/InviteWorkspaceDialog';
import { useWorkspaceActions } from '~teacher/components/workspaces/WorkspaceMembers/WorkspaceMembersTable/useWorkspaceActions';
import { useGetWorkspaceDetail } from '~teacher/hook/workspaces';
import { useGetWorkspaceRole } from '~teacher/hook/workspaces/useGetWorkspaceRole';
import { useWorkspaceMembers } from '~teacher/hook/workspaces/useWorkspaceMembers';

import { WorkspaceMember } from '../../../../api/workspaces/getWorkspaceMembers';
import { WorkspaceMembersTable } from './WorkspaceMembersTable';

interface Props {
  workspaceId: string;
}

type WorkspaceMemberTableItem = WorkspaceMember & {
  // When displaying in lists we want to know if this is the current user
  isYou: boolean;
};

/**
 * Hook that returns all the workspace members, and also adds a `isYou` flag
 * @param workspaceId the id of the workspace to return members from
 */
function useWorkspaceMemberTableData({ workspaceId }: { workspaceId: string }) {
  const workspaceMembersQuery = useWorkspaceMembers({
    workspaceId,
  });
  const userRoleQuery = useGetWorkspaceRole({
    workspaceId,
    select: (workspaceMember) => workspaceMember.id,
  });

  const data = React.useMemo(() => {
    return (workspaceMembersQuery?.data ?? []).map<WorkspaceMemberTableItem>(
      (workspaceMember) => ({
        ...workspaceMember,
        isYou: workspaceMember.id === userRoleQuery.data,
      })
    );
  }, [userRoleQuery.data, workspaceMembersQuery?.data]);

  return {
    data: data,
    workspaceMembersQuery,
    requestUser: userRoleQuery,
  };
}

export function WorkspaceMembers({ workspaceId }: Props) {
  const {
    isDisabled,
    tableActions,
    onTransfer,
    onRemove,
    isTransferOpen,
    isRemoveOpen,
    onRemoveClose,
    onTransferClose,
    username,
  } = useWorkspaceActions(workspaceId);
  const [showInviteDialog, setShowInviteDialog] = React.useState(false);
  const { data } = useWorkspaceMemberTableData({ workspaceId });
  return (
    <>
      <WorkspaceMembersTable
        workspaceId={workspaceId}
        actions={tableActions}
        data={data}
        initialSort={{ id: 'fullName', desc: false }}
        emptyState={<></>}
      >
        <Container style={{ alignItems: 'flex-end', margin: '0 0 20px 0' }}>
          <SmallButton
            appearance={'primary'}
            onClick={() => setShowInviteDialog(true)}
          >
            {translate('Invite')}
          </SmallButton>
        </Container>
      </WorkspaceMembersTable>
      <WorkspaceInviteDialog
        workspaceId={workspaceId}
        open={showInviteDialog}
        setOpen={setShowInviteDialog}
      />
      <RemoveDialog
        open={isRemoveOpen}
        onClose={onRemoveClose}
        action={onRemove}
        isDisabled={isDisabled}
        workspaceId={workspaceId}
        name={username ?? ''}
      />
      <TransferDialog
        open={isTransferOpen}
        onClose={onTransferClose}
        action={onTransfer}
        isDisabled={isDisabled}
        workspaceId={workspaceId}
        name={username ?? ''}
      />
    </>
  );
}

const RemoveDialog = ({
  open,
  onClose,
  name,
  action,
  isDisabled,
  workspaceId,
}: {
  open: boolean;
  onClose: () => void;
  name: string;
  action: () => void;
  isDisabled: boolean;
  workspaceId: string;
}) => {
  const { data } = useGetWorkspaceDetail({ workspaceId });
  return (
    <DialogConfirmation
      actions={[
        <UIButton key="cancel" isDisabled={isDisabled} onClick={onClose}>
          {translate('Cancel')}
        </UIButton>,
        <UIButton
          key="primary"
          isDisabled={isDisabled}
          appearance="risky"
          onClick={action}
        >
          {translate('Remove')}
        </UIButton>,
      ]}
      open={open}
      onClose={onClose}
      id={'remove-member-dialog'}
      title={`Remove ${name} From This library?`}
    >
      <span className="text-blue-900">
        {translate(
          '"{0}" will no longer have access to "{1}" and its quiz library. Any folders or quizzes they have inside of the library will remain.'
        ).format(name, data?.name ?? '')}
      </span>
    </DialogConfirmation>
  );
};

const TransferDialog = ({
  open,
  onClose,
  name,
  action,
  isDisabled,
  workspaceId,
}: {
  open: boolean;
  onClose: () => void;
  name: string;
  action: () => void;
  isDisabled: boolean;
  workspaceId: string;
}) => {
  const { data } = useGetWorkspaceDetail({ workspaceId });
  return (
    <DialogConfirmation
      actions={[
        <UIButton key="cancel" isDisabled={isDisabled} onClick={onClose}>
          {translate('Cancel')}
        </UIButton>,
        <UIButton
          key="primary"
          isDisabled={isDisabled}
          appearance="risky"
          onClick={action}
        >
          {translate('Transfer')}
        </UIButton>,
      ]}
      open={open}
      onClose={onClose}
      id={'transfer-ownership-dialog'}
      title={translate('Transfer Ownership')}
    >
      <span className="text-blue-900">
        {translate(
          'Are you sure you want to make "{0}" the Owner of "{1}"? You can’t undo this action.'
        ).format(name, data?.name ?? '')}
      </span>
    </DialogConfirmation>
  );
};
